import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { ChevronLeft, Plus, Minus, Send, Check, AlertTriangle, Trash2, ShoppingBag, Save } from 'lucide-react';
import { CONVERSION } from '../../constants/productos';
import { fmtMoney } from '../../utils/money';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function AdminAsignaciones() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isPropietario = user?.role === 'propietario';

  const [puntosVenta, setPuntosVenta] = useState([]);
  const [asignaciones, setAsignaciones] = useState([]);
  const [listasPrecios, setListasPrecios] = useState([]);
  const [fecha, setFecha] = useState(() => new Date().toISOString().split('T')[0]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(null);
  const [error, setError] = useState('');

  const [pollosInput, setPollosInput] = useState({});
  const [listaInput, setListaInput] = useState({});

  // Inventario global (propietario only)
  const [invGlobal, setInvGlobal] = useState({ pollos_global: '', precio_compra_kg: '', kg_totales: '' });
  const [savingGlobal, setSavingGlobal] = useState(false);
  const [savedGlobal, setSavedGlobal] = useState(false);

  useEffect(() => {
    loadData();
  }, [fecha]);

  const loadData = async () => {
    setLoading(true);
    try {
      const requests = [
        axios.get(`${API}/puntos-venta`),
        axios.get(`${API}/asignaciones?fecha=${fecha}`),
        axios.get(`${API}/listas-precios`),
      ];
      if (isPropietario) requests.push(axios.get(`${API}/inventario-global/${fecha}`));

      const [puntosRes, asignRes, listasRes, invRes] = await Promise.all(requests);
      setPuntosVenta(puntosRes.data.sort((a, b) => a.name.localeCompare(b.name, 'es')));
      setAsignaciones(asignRes.data);
      setListasPrecios(listasRes.data.sort((a, b) => a.nombre.localeCompare(b.nombre, 'es')));

      if (invRes?.data && Object.keys(invRes.data).length > 0) {
        const d = invRes.data;
        // Derive kg_totales: stored field first, then fallback for legacy records
        const kgTotales = d.kg_totales || (d.peso_promedio_kg && d.pollos_global ? d.peso_promedio_kg * d.pollos_global : 0);
        setInvGlobal({
          pollos_global: String(d.pollos_global || ''),
          precio_compra_kg: String(d.precio_compra_kg || ''),
          kg_totales: kgTotales ? String(kgTotales) : '',
        });
      } else {
        setInvGlobal({ pollos_global: '', precio_compra_kg: '', kg_totales: '' });
      }

      const inputs = {};
      const listas = {};
      for (const a of asignRes.data) {
        inputs[a.punto_venta_id] = a.pollos_enteros;
        if (a.lista_precios_id) listas[a.punto_venta_id] = a.lista_precios_id;
      }
      setPollosInput(inputs);
      setListaInput(listas);
    } catch (err) {
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveGlobal = async () => {
    setSavingGlobal(true);
    try {
      await axios.put(`${API}/inventario-global/${fecha}`, {
        pollos_global: parseFloat(invGlobal.pollos_global) || 0,
        precio_compra_kg: parseFloat(invGlobal.precio_compra_kg) || 0,
        kg_totales: parseFloat(invGlobal.kg_totales) || 0,
      });
      setSavedGlobal(true);
      setTimeout(() => setSavedGlobal(false), 2000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al guardar inventario global');
    } finally {
      setSavingGlobal(false);
    }
  };

  const handleAsignar = async (puntoId) => {
    if (!(puntoId in pollosInput)) {
      setError('Ingresa la cantidad de pollos');
      return;
    }
    const cantidad = pollosInput[puntoId];
    setError('');
    setSubmitting(puntoId);
    try {
      await axios.post(`${API}/asignaciones`, {
        punto_venta_id: puntoId,
        fecha,
        pollos_enteros: cantidad,
        lista_precios_id: listaInput[puntoId] || null,
      });
      await loadData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al asignar');
    } finally {
      setSubmitting(null);
    }
  };

  const handleEliminar = async (asignacionId) => {
    try {
      await axios.delete(`${API}/asignaciones/${asignacionId}`);
      await loadData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al eliminar');
    }
  };

  const incrementPollos = (puntoId) => {
    setPollosInput(prev => ({ ...prev, [puntoId]: (prev[puntoId] || 0) + 1 }));
  };

  const decrementPollos = (puntoId) => {
    setPollosInput(prev => ({ ...prev, [puntoId]: Math.max(0, (prev[puntoId] || 0) - 1) }));
  };

  const getAsignacion = (puntoId) => asignaciones.find(a => a.punto_venta_id === puntoId);

  const calcPiezas = (pollos) => pollos * 11;

  const getTotalPollos = () => {
    return puntosVenta.reduce((sum, p) => sum + (pollosInput[p.id] || 0), 0);
  };

  const getListaNombre = (listaId) => {
    const l = listasPrecios.find(lp => lp.id === listaId);
    return l ? l.nombre : '';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <span className="inline-block w-8 h-8 border-2 border-zinc-300 border-t-zinc-600 rounded-full animate-spin" />
      </div>
    );
  }

  const totalPollos = getTotalPollos();

  return (
    <div className="min-h-screen bg-zinc-50 flex flex-col" data-testid="admin-asignaciones-page">
      {/* Header */}
      <div className="bg-white px-4 py-3 border-b border-zinc-200">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="w-10 h-10 flex items-center justify-center border border-zinc-300 rounded-lg"
            data-testid="btn-back"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-zinc-900">Asignar Pollos</h1>
            <p className="text-sm text-zinc-500">Carga de pollos enteros por punto de venta</p>
          </div>
        </div>
      </div>

      <div className="px-4 py-4 space-y-4">
        {/* Date picker */}
        <div className="bg-white rounded-xl border border-zinc-200 p-4">
          <label className="text-sm font-medium text-zinc-600 mb-2 block">Fecha</label>
          <input
            type="date"
            value={fecha}
            onChange={(e) => setFecha(e.target.value)}
            className="w-full h-12 px-4 border border-zinc-300 rounded-xl text-lg font-medium"
            data-testid="input-fecha"
          />
        </div>

        {/* Conversion reference */}
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
          <p className="text-sm font-medium text-amber-800 mb-2">Por cada pollo entero se generan:</p>
          <div className="flex flex-wrap gap-2">
            {Object.entries(CONVERSION).map(([clave, info]) => (
              <span key={clave} className="text-xs bg-white px-2 py-1 rounded-lg text-amber-700 border border-amber-200">
                {info.factor} {info.nombre}
              </span>
            ))}
          </div>
          <p className="text-xs text-amber-600 mt-2">= 11 piezas por pollo</p>
        </div>

        {/* Inventario Global (propietario only) */}
        {isPropietario && (() => {
          const pollosGlobal = parseFloat(invGlobal.pollos_global) || 0;
          const precioKg = parseFloat(invGlobal.precio_compra_kg) || 0;
          const kgTotales = parseFloat(invGlobal.kg_totales) || 0;
          const pesoPromedio = (pollosGlobal > 0 && kgTotales > 0) ? kgTotales / pollosGlobal : 0;
          const totalCompra = kgTotales * precioKg;
          const diff = pollosGlobal - totalPollos;
          const match = pollosGlobal > 0 && diff === 0;
          const mismatch = pollosGlobal > 0 && diff !== 0;

          return (
            <div className="bg-white rounded-xl border border-zinc-200 overflow-hidden">
              <div className="px-4 py-3 border-b border-zinc-100 flex items-center gap-2">
                <ShoppingBag className="w-4 h-4 text-amber-600" />
                <span className="font-bold text-zinc-900">Inventario Global del Día</span>
              </div>
              <div className="p-4 space-y-4">
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="text-xs text-zinc-500 mb-1 block">Pollos comprados</label>
                    <input
                      type="number"
                      inputMode="numeric"
                      min="0"
                      value={invGlobal.pollos_global}
                      onChange={(e) => setInvGlobal(prev => ({ ...prev, pollos_global: e.target.value }))}
                      placeholder="0"
                      className="w-full h-11 px-3 border border-zinc-300 rounded-lg text-lg font-bold text-center focus:outline-none focus:border-zinc-600"
                      data-testid="input-pollos-global"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-zinc-500 mb-1 block">kg totales</label>
                    <input
                      type="number"
                      inputMode="decimal"
                      min="0"
                      step="0.1"
                      value={invGlobal.kg_totales}
                      onChange={(e) => setInvGlobal(prev => ({ ...prev, kg_totales: e.target.value }))}
                      placeholder="0.0"
                      className="w-full h-11 px-3 border border-zinc-300 rounded-lg text-lg font-bold text-center focus:outline-none focus:border-zinc-600"
                      data-testid="input-kg-totales"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-zinc-500 mb-1 block">Precio/kg ($)</label>
                    <input
                      type="number"
                      inputMode="decimal"
                      min="0"
                      step="0.5"
                      value={invGlobal.precio_compra_kg}
                      onChange={(e) => setInvGlobal(prev => ({ ...prev, precio_compra_kg: e.target.value }))}
                      placeholder="0.00"
                      className="w-full h-11 px-3 border border-zinc-300 rounded-lg text-lg font-bold text-center focus:outline-none focus:border-zinc-600"
                      data-testid="input-precio-kg"
                    />
                  </div>
                </div>

                {pesoPromedio > 0 && (
                  <div className="bg-zinc-50 rounded-lg px-4 py-2 flex justify-between items-center">
                    <span className="text-sm text-zinc-600">Promedio del día:</span>
                    <span className="text-lg font-bold text-zinc-900">{pesoPromedio.toFixed(2)} kg/pollo</span>
                  </div>
                )}

                {totalCompra > 0 && (
                  <div className="bg-zinc-50 rounded-lg px-4 py-2 flex justify-between items-center">
                    <span className="text-sm text-zinc-600">Total a pagar:</span>
                    <span className="text-lg font-bold text-zinc-900">
                      {fmtMoney(totalCompra)}
                    </span>
                  </div>
                )}

                {match && (
                  <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg px-4 py-2 text-sm text-green-700">
                    <Check className="w-4 h-4 shrink-0" />
                    <span>Total asignado coincide con inventario global ({totalPollos} pollos)</span>
                  </div>
                )}
                {mismatch && (
                  <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg px-4 py-2 text-sm text-red-700">
                    <AlertTriangle className="w-4 h-4 shrink-0" />
                    <span>
                      {diff > 0
                        ? `Faltan ${diff} pollos por asignar (global: ${pollosGlobal} / asignados: ${totalPollos})`
                        : `Se asignaron ${Math.abs(diff)} pollos de más (global: ${pollosGlobal} / asignados: ${totalPollos})`}
                    </span>
                  </div>
                )}

                <button
                  onClick={handleSaveGlobal}
                  disabled={savingGlobal || pollosGlobal === 0}
                  className="w-full h-11 bg-amber-600 text-white rounded-xl font-medium flex items-center justify-center gap-2 disabled:opacity-50"
                  data-testid="btn-save-global"
                >
                  {savingGlobal ? (
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : savedGlobal ? (
                    <><Check className="w-4 h-4" /> Guardado</>
                  ) : (
                    <><Save className="w-4 h-4" /> Guardar inventario global</>
                  )}
                </button>
              </div>
            </div>
          );
        })()}

        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">{error}</div>
        )}

        {/* Puntos de venta */}
        {puntosVenta.map((punto) => {
          const asignacion = getAsignacion(punto.id);
          const pollos = pollosInput[punto.id] || 0;
          const isConfirmada = asignacion?.status === 'confirmada';
          const isPendiente = asignacion?.status === 'pendiente';

          return (
            <div key={punto.id} className="bg-white rounded-xl border border-zinc-200 overflow-hidden" data-testid={`punto-${punto.id}`}>
              <div className="px-4 py-3 border-b border-zinc-100 flex items-center justify-between">
                <div>
                  <h3 className="font-bold text-zinc-900">{punto.name}</h3>
                  {punto.address && <p className="text-xs text-zinc-500">{punto.address}</p>}
                </div>
                {isConfirmada && (
                  <span className="flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                    <Check className="w-3 h-3" /> Confirmada
                  </span>
                )}
                {isPendiente && (
                  <span className="flex items-center gap-1 px-2 py-1 bg-orange-100 text-orange-700 rounded-full text-xs font-medium">
                    <AlertTriangle className="w-3 h-3" /> Pendiente
                  </span>
                )}
              </div>

              <div className="p-4">
                {isConfirmada ? (
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-zinc-600">Pollos asignados:</span>
                      <span className="text-xl font-bold">{asignacion.pollos_enteros}</span>
                    </div>
                    {asignacion.lista_precios_id && (
                      <div className="flex justify-between items-center">
                        <span className="text-zinc-600">Lista de precios:</span>
                        <span className="font-medium text-sm">{getListaNombre(asignacion.lista_precios_id)}</span>
                      </div>
                    )}
                    <div className="flex justify-between items-center">
                      <span className="text-zinc-600">Confirmado por:</span>
                      <span className="font-medium">{asignacion.confirmado_por_nombre}</span>
                    </div>
                    {asignacion.diferencias && Object.values(asignacion.diferencias).some(d => d !== 0) && (
                      <div className="bg-red-50 rounded-lg p-3">
                        <p className="text-sm font-medium text-red-700 mb-1">Diferencias:</p>
                        {Object.entries(asignacion.diferencias).map(([clave, diff]) => {
                          if (diff === 0) return null;
                          const nombre = CONVERSION[clave]?.nombre || clave;
                          return (
                            <p key={clave} className="text-xs text-red-600">{nombre}: {diff > 0 ? '+' : ''}{diff}</p>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* Lista de precios selector */}
                    <div>
                      <label className="text-sm text-zinc-600 mb-1 block">Lista de precios</label>
                      <select
                        value={listaInput[punto.id] || ''}
                        onChange={(e) => setListaInput(prev => ({ ...prev, [punto.id]: e.target.value }))}
                        className="w-full h-11 px-3 border border-zinc-300 rounded-lg text-sm bg-white"
                        data-testid={`select-lista-${punto.id}`}
                      >
                        <option value="">Sin lista asignada</option>
                        {listasPrecios.map(l => (
                          <option key={l.id} value={l.id}>{l.nombre} ({l.peso_promedio} kg)</option>
                        ))}
                      </select>
                    </div>

                    {/* Pollos input */}
                    <div className="flex items-center justify-between">
                      <span className="text-zinc-700 font-medium">Pollos enteros:</span>
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => decrementPollos(punto.id)}
                          disabled={pollos === 0}
                          className="w-12 h-12 flex items-center justify-center border border-zinc-300 rounded-lg active:bg-zinc-100 disabled:opacity-30"
                          data-testid={`btn-minus-${punto.id}`}
                        >
                          <Minus className="w-5 h-5" />
                        </button>
                        <input
                          type="number"
                          inputMode="numeric"
                          min="0"
                          value={pollos === 0 ? '' : pollos}
                          onChange={(e) => {
                            const val = parseInt(e.target.value, 10);
                            setPollosInput(prev => ({ ...prev, [punto.id]: isNaN(val) || val < 0 ? 0 : val }));
                          }}
                          onBlur={(e) => {
                            if (e.target.value === '') setPollosInput(prev => ({ ...prev, [punto.id]: 0 }));
                          }}
                          placeholder="0"
                          className="w-16 text-center text-3xl font-bold text-zinc-900 border border-zinc-300 rounded-lg h-12 focus:outline-none focus:border-zinc-600"
                          data-testid={`input-pollos-${punto.id}`}
                        />
                        <button
                          onClick={() => incrementPollos(punto.id)}
                          className="w-12 h-12 flex items-center justify-center border border-zinc-300 rounded-lg active:bg-zinc-100"
                          data-testid={`btn-plus-${punto.id}`}
                        >
                          <Plus className="w-5 h-5" />
                        </button>
                      </div>
                    </div>

                    {pollos > 0 && (
                      <div className="bg-zinc-50 rounded-lg p-3 text-sm text-zinc-600">
                        <p className="font-medium mb-1">Piezas esperadas ({calcPiezas(pollos)} total):</p>
                        <div className="grid grid-cols-2 gap-1">
                          {Object.entries(CONVERSION).map(([clave, info]) => (
                            <span key={clave}>{info.nombre}: <strong>{pollos * info.factor}</strong></span>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="flex gap-2">
                      <button
                        onClick={() => handleAsignar(punto.id)}
                        disabled={submitting === punto.id || !(punto.id in pollosInput)}
                        className="flex-1 h-12 bg-zinc-900 text-white rounded-xl font-medium flex items-center justify-center gap-2 disabled:opacity-50"
                        data-testid={`btn-asignar-${punto.id}`}
                      >
                        {submitting === punto.id ? (
                          <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <>
                            <Send className="w-4 h-4" />
                            {isPendiente ? 'Actualizar' : 'Asignar'}
                          </>
                        )}
                      </button>
                      {isPendiente && (
                        <button
                          onClick={() => handleEliminar(asignacion.id)}
                          className="w-12 h-12 flex items-center justify-center border border-red-200 rounded-xl text-red-500 hover:bg-red-50"
                          data-testid={`btn-eliminar-${punto.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}

        {/* Total de pollos */}
        <div className="bg-zinc-800 rounded-xl p-4 flex items-center justify-between" data-testid="total-pollos">
          <span className="text-white font-medium">Total pollos todos los puntos</span>
          <div className="text-right">
            <span className="text-3xl font-bold text-white">{totalPollos}</span>
            <p className="text-xs text-zinc-400">{calcPiezas(totalPollos)} piezas</p>
          </div>
        </div>
      </div>
    </div>
  );
}
